import { Link, useLocation } from "react-router-dom";
import { Gift, Gamepad2, Crown, Wallet, Headphones } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Sticky mobile-style bottom navigation.
 *
 * Layout: 5 items — 2 left, elevated CENTER crown (home), 2 right.
 * Hidden on admin routes (they have their own sidebar).
 */
export default function BottomNav() {
  const location = useLocation();
  const { user } = useAuth();
  const isAuthed = user && user !== false;
  if (!isAuthed || location.pathname.startsWith("/admin") || location.pathname === "/support") return null;

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="bottom-nav"
      className="bottom-nav-shell"
    >
      <div className="bottom-nav-frame">
        <div className="relative rounded-2xl border border-slate-200 bg-white/95 px-1.5 pb-1.5 pt-2 text-slate-700 backdrop-blur-xl shadow-2xl shadow-slate-900/15 sm:px-2 sm:pb-2 sm:pt-2.5">
          <div className="grid min-h-[4rem] grid-cols-5 items-end sm:min-h-[4.35rem]">
            <NavItem to="/dashboard" icon={Gift} label="Rewards" active={isActive("/dashboard")} testid="bottomnav-rewards" />
            <NavItem to="/games" icon={Gamepad2} label="Games" active={isActive("/games")} testid="bottomnav-games" />

            {/* Center elevated crown — Home */}
            <div className="flex justify-center">
              <Link
                to="/"
                data-testid="bottomnav-home"
                aria-label="Home"
                className={`relative -mt-5 grid h-12 w-12 shrink-0 place-items-center rounded-full transition-colors shadow-xl sm:-mt-6 sm:h-14 sm:w-14 ${
                  isActive("/")
                    ? "bg-[#2F6FA5] text-white shadow-[#2F6FA5]/35 ring-4 ring-[#2F6FA5]/25"
                    : "bg-white text-[#2F6FA5] border-2 border-[#2F6FA5]/35 shadow-[#2F6FA5]/20 ring-4 ring-[#2F6FA5]/10 hover:bg-[#EAF4FC]"
                }`}
              >
                <Crown className="h-5 w-5 sm:h-6 sm:w-6" />
              </Link>
            </div>

            <NavItem to="/wallet" icon={Wallet} label="Wallets" active={isActive("/wallet")} testid="bottomnav-wallets" />
            <NavItem
              to="/support"
              icon={Headphones}
              label="Support"
              active={isActive("/support")}
              state={{ from: location.pathname }}
              testid="bottomnav-support"
            />
          </div>
        </div>
      </div>
    </nav>
  );
}

function NavItem({ to, icon: Icon, label, active, onClick, state, testid }) {
  return (
    <Link
      to={to}
      state={state}
      onClick={onClick}
      data-testid={testid}
      className={`flex min-w-0 flex-col items-center gap-0.5 py-1 transition-colors sm:gap-1 sm:py-1.5 ${
        active ? "text-[#2F6FA5]" : "text-slate-500 hover:text-slate-950"
      }`}
    >
      <Icon className="h-4 w-4 shrink-0 sm:h-[18px] sm:w-[18px]" />
      <span className="max-w-full truncate text-[9.5px] font-semibold sm:text-[10.5px]">{label}</span>
    </Link>
  );
}
